import { Invoice } from '@wonderschool/common-base-types';
import { Button, Dialog, showToast, ToastTypeEnum } from '@wonderschool/common-base-ui';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { updateInvoiceStatus } from '../../../../api/firebase/invoices';
import { InvoiceStatus } from '../../../../helpers/invoices';
import { USER_STATUS } from '../../../../helpers/userStatus';
import { logError } from '../../../../rollbar';
import InvoiceActionForm from './InvoiceActionForm';
import type { InvoiceUpdateOptions } from './InvoiceDetailContainer';

export function invoiceHasStatusActions(invoice: Invoice): boolean {
  if (!invoice) return false;
  const inv = invoice as any;
  return (
    !(invoice.status === InvoiceStatus.OPEN && inv.userStatus === USER_STATUS.PAID) &&
    !(invoice.status === InvoiceStatus.OPEN && inv.userStatus === USER_STATUS.PROCESSING) &&
    !(invoice.status === InvoiceStatus.OPEN && inv.userStatus === USER_STATUS.TRANSFERRING) &&
    !inv.alreadyPaid &&
    !inv.manuallyPaid &&
    !([InvoiceStatus.PROCESSING, InvoiceStatus.VOID, InvoiceStatus.PAID] as string[]).includes(invoice.status as string)
  );
}

type InvoiceStatusDialogProps = {
  invoice: Invoice;
  invoiceType: string;
  isOpen: boolean;
  onClose: () => void;
  onInvoiceUpdate: (data: Partial<Invoice>, options?: InvoiceUpdateOptions) => Promise<void> | void;
  onSuccess?: (updatedData: Partial<Invoice>) => void;
};

export function InvoiceStatusDialog({
  invoice,
  invoiceType,
  isOpen,
  onClose,
  onInvoiceUpdate,
  onSuccess,
}: InvoiceStatusDialogProps) {
  const { t } = useTranslation();
  const [isPendingUpdateStatus, setIsPendingUpdateStatus] = useState(false);
  const [showUpdateStatusError, setShowUpdateStatusError] = useState(false);
  const [statusUpdatedWithMetadataError, setStatusUpdatedWithMetadataError] = useState(false);
  const [showPendingUpdateStatusModal, setShowPendingUpdateStatusModal] = useState(false);
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const isSavingStatusRef = useRef(false);

  const onStatusSave = async (data) => {
    if (isSavingStatusRef.current) return;
    isSavingStatusRef.current = true;
    setIsSavingStatus(true);
    const { status, ...dataWithoutStatus } = data;
    const stripeInvoiceId = (invoice as any).stripe?.invoiceId;

    try {
      if (stripeInvoiceId && status === 'void') {
        onClose();
        setIsPendingUpdateStatus(true);
        setShowPendingUpdateStatusModal(true);

        let updateError;
        try {
          const { error } = await updateInvoiceStatus(stripeInvoiceId, data.status);
          updateError = error;
        } catch (error) {
          updateError = error;
        }

        setIsPendingUpdateStatus(false);

        if (updateError) {
          setShowUpdateStatusError(true);
        } else {
          try {
            await onInvoiceUpdate(dataWithoutStatus, { suppressErrorToast: true });
            onSuccess?.({ status: InvoiceStatus.VOID, ...dataWithoutStatus });
            setShowPendingUpdateStatusModal(false);
          } catch (error) {
            logError('InvoiceStatusDialog: failed saving voided invoice metadata', error);
            onSuccess?.({ status: InvoiceStatus.VOID, ...dataWithoutStatus });
            setStatusUpdatedWithMetadataError(true);
            setShowUpdateStatusError(true);
          }
        }
      } else {
        try {
          await onInvoiceUpdate(data, { suppressErrorToast: true });
          onSuccess?.(data);
          onClose();
        } catch (error) {
          logError('InvoiceStatusDialog: failed saving invoice status', error);
          showToast(
            ToastTypeEnum.Error,
            t(invoiceType === 'recurring' ? 'billing.invoicePlan.saveFailed' : 'billing.invoice.saveFailed')
          );
        }
      }
    } finally {
      isSavingStatusRef.current = false;
      setIsSavingStatus(false);
    }
  };

  return (
    <>
      <Dialog
        isOpen={showPendingUpdateStatusModal}
        onClose={() => {
          if (isPendingUpdateStatus) return;
          setShowPendingUpdateStatusModal(false);
          setShowUpdateStatusError(false);
          setStatusUpdatedWithMetadataError(false);
        }}
      >
        <Dialog.Panel>
          {showUpdateStatusError && (
            <div className="update-status-error">
              <span>
                {t(
                  statusUpdatedWithMetadataError
                    ? 'billing.invoiceDetails.metadataUpdateError'
                    : 'billing.invoiceDetails.updateStatusError'
                )}
              </span>
              <Button
                extraClasses="mt-4"
                primary
                label={t('billing.invoiceStatus.closeAction')}
                onClick={() => {
                  setShowPendingUpdateStatusModal(false);
                  setShowUpdateStatusError(false);
                  setStatusUpdatedWithMetadataError(false);
                }}
              />
            </div>
          )}
          {isPendingUpdateStatus && <span>{t('billing.invoiceDetails.updateStatusPending')}</span>}
        </Dialog.Panel>
      </Dialog>

      <Dialog isOpen={isOpen} onClose={onClose}>
        <Dialog.Panel>
          <Dialog.Title>{t('billing.invoiceStatus.dialogTitle')}</Dialog.Title>
          <InvoiceActionForm
            invoice={invoice}
            invoiceType={invoiceType}
            loading={isSavingStatus}
            onCancel={onClose}
            onSave={onStatusSave}
          />
        </Dialog.Panel>
      </Dialog>
    </>
  );
}
